import { isUUID } from 'class-validator';

export type AnalyticsPropValues = Record<string, boolean | number | string>;

export type AnalyticsPropSpec =
  | { type: 'boolean' }
  | { type: 'number' }
  | { type: 'enum'; values: readonly string[] }
  /** The id of a record that is not about a patient; see NON_PATIENT_RECORD_ID_PROPS. */
  | { type: 'uuid' };

export interface AnalyticsEventSpec {
  anonymous: boolean;
  props: Readonly<Record<string, AnalyticsPropSpec>>;
}

/** The only props that may carry a uuid: ids of records with no patient in them. */
export const NON_PATIENT_RECORD_ID_PROPS = ['announcementId'] as const;

const announcementEvent = {
  anonymous: false,
  props: { announcementId: { type: 'uuid' } },
} as const;

/**
 * Every analytics event a client may send. Review each number prop for what it
 * could reveal about a patient. Events, props and enum values may be added. Once
 * a release has shipped one, installed apps keep sending it, so it is never
 * removed or renamed.
 */
export const ANALYTICS_EVENT_CATALOGUE = {
  login_method_selected: {
    anonymous: true,
    props: { method: { type: 'enum', values: ['mitid', 'email', 'qr_code'] } },
  },
  login_completed: {
    anonymous: false,
    props: {
      method: {
        type: 'enum',
        values: ['mitid', 'email', 'qr_code', 'google', '1login'],
      },
    },
  },
  dermoscope_opened: {
    anonymous: true,
    props: {
      source: { type: 'enum', values: ['login', 'offline', 'home'] },
    },
  },
  announcement_shown: announcementEvent,
  announcement_dismissed: announcementEvent,
  announcement_link_opened: announcementEvent,
} as const satisfies Record<string, AnalyticsEventSpec>;

type Catalogue = typeof ANALYTICS_EVENT_CATALOGUE;

export type AnalyticsEventName = keyof Catalogue;

type PropValue<S> = S extends { type: 'boolean' }
  ? boolean
  : S extends { type: 'number' }
    ? number
    : S extends { type: 'enum'; values: readonly (infer V)[] }
      ? V
      : S extends { type: 'uuid' }
        ? string
        : never;

export type AnalyticsEventProps<N extends AnalyticsEventName> = {
  [K in keyof Catalogue[N]['props']]: PropValue<Catalogue[N]['props'][K]>;
};

export const ANALYTICS_EVENT_NAMES = Object.keys(
  ANALYTICS_EVENT_CATALOGUE,
) as AnalyticsEventName[];

export const ANONYMOUS_ANALYTICS_EVENT_NAMES = ANALYTICS_EVENT_NAMES.filter(
  (name) => ANALYTICS_EVENT_CATALOGUE[name].anonymous,
);

export const ANALYTICS_APPS = ['desktop-web', 'capture'] as const;
export type AnalyticsApp = (typeof ANALYTICS_APPS)[number];

export const ANALYTICS_PLATFORMS = ['web', 'ios', 'android'] as const;
export type AnalyticsPlatform = (typeof ANALYTICS_PLATFORMS)[number];

const matchesPropSpec = (spec: AnalyticsPropSpec, value: unknown) => {
  switch (spec.type) {
    case 'boolean':
      return typeof value === 'boolean';
    case 'number':
      return typeof value === 'number' && Number.isFinite(value);
    case 'enum':
      return typeof value === 'string' && spec.values.includes(value);
    case 'uuid':
      return typeof value === 'string' && isUUID(value);
  }
};

/**
 * Why `props` do not fit the event's catalogue entry, or undefined when they do.
 * Declared props may be missing, so a prop added later does not reject older apps.
 */
export function analyticsPropsProblem(
  name: string,
  props: unknown,
  catalogue: Record<string, AnalyticsEventSpec> = ANALYTICS_EVENT_CATALOGUE,
): string | undefined {
  if (!Object.hasOwn(catalogue, name)) return 'unknown event';
  const spec = catalogue[name];
  if (typeof props !== 'object' || props === null || Array.isArray(props))
    return 'props must be an object';
  for (const [key, value] of Object.entries(props)) {
    if (!Object.hasOwn(spec.props, key)) return 'unknown prop';
    if (!matchesPropSpec(spec.props[key], value)) return 'invalid prop value';
  }
  return undefined;
}
